import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

import type { GettingStartedConfig } from 'app/features/admin/widgets/widgets.model';
import { ProgressRingComponent } from '../progress-ring.component';

/** Which skin the panel renders in. One component, two shapes. */
export type GettingStartedPanelMode = 'banner' | 'floating';

/**
 * Flattened, fully-resolved step as the panel needs it. The service (GettingStartedService)
 * derives this from `GettingStartedStep` + live completion evaluation + per-user skip state;
 * the panel never re-derives any of it.
 */
export interface GettingStartedStepView {
  id: string;
  index: number;
  title: string;
  description?: string;
  buttonLabel: string;
  state: 'completed' | 'skipped' | 'open';
  showStartButton: boolean;
  skippable: boolean;
}

/** Template-only decoration of a step view. Nothing is computed inside the template. */
interface StepRow extends GettingStartedStepView {
  /** '01', '02', ... */
  indexLabel: string;
  isCompleted: boolean;
  isSkipped: boolean;
  /** Start is dropped on completed steps, kept on skipped ones (the user may still act). */
  showStart: boolean;
  /** Skip is only offered while the step is genuinely open. */
  showSkip: boolean;
  startLabel: string;
}

/**
 * "Aan de slag" onboarding panel — purely presentational.
 *
 * It renders what it is given and emits intent; it does no data fetching and writes no
 * state. Completion, skip state, visibility and persistence all live in
 * `GettingStartedService`.
 *
 * Two skins via `mode`:
 * - `banner`   — full-width dashboard block, always expanded. It has no collapse affordance and
 *                ignores `collapsed`: on its own page it IS the onboarding, and a folded-away
 *                banner reads as an empty strip above the widgets.
 * - `floating` — 278px collapsed card / 599px expanded panel for the app shell, where collapsing
 *                is the point: it follows the user across every page.
 *
 * The surface is dark navy in BOTH schemes (it is a brand surface, not a page surface). Every
 * colour goes through a `--dd-gs-*` token with a light default on `:host` and, where the design
 * sheet actually flips, an override under `:host-context(.dark)` — the `.dark` class that
 * `layout.component.ts` puts on `<html>`/`<body>`. Where a portal token already covers a role in
 * both schemes, the `--dd-gs-*` token binds to it rather than carrying its own hex: the ring
 * value and the marker glyph to the accent roles (`--dd-accent-border` / `--dd-accent-fg`), the
 * marker plate to `--dd-badge-accent-bg`, the muted card text and the skip label to
 * `--dd-accent`, and both the card edge and the folded-row rule to `--dd-border-default`.
 */
@Component({
  selector: 'app-getting-started-panel',
  imports: [MatIconModule, NgTemplateOutlet, ProgressRingComponent],
  template: `
    <section
      class="gs-panel overflow-hidden rounded-xl"
      [class]="widthClass()"
      [class.gs-panel--floating-collapsed]="!isBanner() && collapsed()"
      data-cy="getting-started-panel">
      <div class="flex items-start gap-2" [class]="headerPaddingClass()">
        @if (isBanner()) {
          <!-- Not a button: the dashboard banner never collapses, so there is nothing to toggle.
               gap-16 = the 64px Figma gutter between the copy block and the progress readout. It
               only applies once they sit side by side. -->
          <div
            class="flex min-w-0 flex-1 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-16"
            data-cy="getting-started-header">
            <span class="flex min-w-0 flex-col gap-1.5">
              <!-- text-3xl is 1.5rem in this project's scale (tailwind.config.js:78) = the 24px
                   Figma headline. text-2xl would be 20px. -->
              <span class="gs-title font-heading text-3xl font-medium leading-[1.2]">{{ config().title }}</span>
              @if (config().subtitle) {
                <span class="gs-subtitle text-base leading-normal">{{ config().subtitle }}</span>
              }
            </span>
            <span class="flex shrink-0 items-center gap-2">
              <app-progress-ring [value]="completedCount()" [max]="totalCount()" [ariaLabel]="ringAriaLabel()" />
              <span class="gs-progress whitespace-nowrap text-base font-medium leading-none">{{ progressLabel() }}</span>
            </span>
          </div>
        } @else {
          <button
            type="button"
            class="gs-toggle flex min-w-0 flex-1 items-start gap-4 rounded-[10px] text-left"
            [attr.aria-expanded]="!collapsed()"
            (click)="toggle.emit()"
            data-cy="getting-started-toggle">
            <span class="flex min-w-0 flex-1 flex-col gap-1.5" [class]="floatingContentPaddingClass()">
              <span class="flex items-center gap-2 pb-1">
                <app-progress-ring [value]="completedCount()" [max]="totalCount()" [ariaLabel]="ringAriaLabel()" />
                <span class="gs-progress whitespace-nowrap text-base font-medium leading-none">{{ progressLabel() }}</span>
              </span>
              <span class="gs-title font-heading text-2xl font-medium leading-[1.2]">{{ config().title }}</span>
              @if (showSubtitle()) {
                <span class="gs-subtitle text-base leading-normal">{{ config().subtitle }}</span>
              }
            </span>
            <span class="gs-panel-toggle-icon flex size-8 shrink-0 items-center justify-center rounded-lg">
              <mat-icon class="gs-chevron icon-size-4" [svgIcon]="toggleIcon()" />
            </span>
          </button>
        }

        @if (config().dismissible) {
          <button
            type="button"
            class="gs-icon-button flex shrink-0 items-center justify-center rounded-lg"
            aria-label="Sluiten"
            (click)="dismiss.emit()"
            data-cy="getting-started-dismiss">
            <mat-icon class="icon-size-5" svgIcon="heroicons_outline:x-mark" />
          </button>
        }
      </div>

      @if (showSteps()) {
        <ul class="flex list-none flex-col gap-3" [class]="stepsPaddingClass()">
          @for (row of primaryRows(); track row.id) {
            <li
              class="gs-step flex flex-wrap items-center gap-x-3 gap-y-3 rounded-lg p-4"
              [class.gs-step--skipped]="row.isSkipped"
              [attr.data-state]="row.state"
              data-cy="getting-started-step">
              <ng-container *ngTemplateOutlet="stepBody; context: { $implicit: row }" />
            </li>
          }

          <!-- Everything past collapseAfter folds into one row. These are ordinary steps that
               keep their own sequence number — the accordion is presentation only. -->
          @if (collapsedRows().length) {
            <li class="gs-group overflow-hidden rounded-lg" data-cy="getting-started-group">
              <button
                type="button"
                class="gs-group-header flex w-full items-center justify-between gap-3 px-4 text-left"
                [class]="groupHeaderPaddingClass()"
                [class.gs-group-header--open]="groupOpen()"
                [attr.aria-expanded]="groupOpen()"
                (click)="toggleGroup()"
                data-cy="getting-started-group-toggle">
                <span class="flex min-w-0 flex-1 items-center gap-3 pr-8">
                  <span class="gs-step-title truncate text-base font-medium leading-[18px]">{{ groupLabel() }}</span>
                  <!-- Figma: badge only once every folded step is genuinely done. -->
                  @if (groupCompleted()) {
                    <span class="gs-badge shrink-0 whitespace-nowrap rounded-lg px-2 py-1 text-sm font-medium">
                      Afgerond
                    </span>
                  }
                </span>
                <span class="gs-group-chevron flex shrink-0 items-center justify-center rounded-lg">
                  <mat-icon class="icon-size-5" [svgIcon]="groupToggleIcon()" />
                </span>
              </button>

              @if (groupOpen()) {
                <ul class="flex list-none flex-col">
                  @for (row of collapsedRows(); track row.id) {
                    <li
                      class="gs-group-step flex flex-wrap items-center gap-x-3 gap-y-3 p-4"
                      [class.gs-step--skipped]="row.isSkipped"
                      [attr.data-state]="row.state"
                      data-cy="getting-started-step">
                      <ng-container *ngTemplateOutlet="stepBody; context: { $implicit: row }" />
                    </li>
                  }
                </ul>
              }
            </li>
          }
        </ul>
      }
    </section>

    <!-- Shared by the expanded steps and the folded ones, so a step looks identical either side
         of the accordion boundary. -->
    <ng-template #stepBody let-row>
      @if (row.isCompleted) {
        <span class="gs-marker gs-marker--done flex shrink-0 items-center justify-center rounded-full">
          <mat-icon class="icon-size-4" svgIcon="heroicons_solid:check" />
          <span class="sr-only">Afgerond</span>
        </span>
      } @else {
        <span
          class="gs-marker flex shrink-0 items-center justify-center rounded-full text-base font-medium tabular-nums"
          aria-hidden="true">
          {{ row.indexLabel }}
        </span>
      }

      <span class="flex min-w-0 flex-1 basis-48 flex-col gap-1 pr-8">
        <span class="gs-step-title break-words text-base font-medium leading-[18px]">{{ row.title }}</span>
        @if (row.description) {
          <span class="gs-step-description break-words text-base leading-normal">{{ row.description }}</span>
        }
      </span>

      <span class="flex w-full shrink-0 items-center justify-end gap-1 sm:w-auto">
        @if (row.isSkipped) {
          <span class="gs-state whitespace-nowrap text-sm font-medium">Overgeslagen</span>
        }
        <!-- Skip UX is not finalised in design yet (#20703 open point): kept as a
             low-key text button next to Start until Julia's design lands. -->
        @if (row.showSkip) {
          <button
            type="button"
            class="gs-skip whitespace-nowrap rounded-lg px-2 py-1.5 text-base font-medium"
            (click)="stepSkip.emit(row.id)"
            data-cy="getting-started-step-skip">
            Overslaan
          </button>
        }
        @if (row.showStart) {
          <button
            type="button"
            class="gs-start flex h-9 items-center whitespace-nowrap rounded-lg px-4 text-base font-medium"
            (click)="stepStart.emit(row.id)"
            data-cy="getting-started-step-start">
            {{ row.startLabel }}
          </button>
        }
      </span>
    </ng-template>
  `,
  styleUrl: './getting-started-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GettingStartedPanelComponent {
  /** `banner` = full-width dashboard header, `floating` = app-shell card. */
  readonly mode = input.required<GettingStartedPanelMode>();

  /** Admin-authored copy + behaviour flags. */
  readonly config = input.required<GettingStartedConfig>();

  /** Resolved steps, already ordered and already carrying their live state. */
  readonly steps = input.required<GettingStartedStepView[]>();

  /**
   * Collapsed = header only. Owned by the caller (persisted in user preferences).
   *
   * `floating` ONLY. The dashboard banner is always expanded: it is the page's own onboarding block,
   * not a card the user tucks away, and a collapsed banner reads as an empty strip above the
   * widgets. The banner therefore renders no toggle affordance and ignores this input — leaving it
   * bound would also let a collapse of the floating panel silently fold the banner, since both
   * placements share one per-user `collapsed` flag keyed on the widget id.
   */
  readonly collapsed = input(true);

  /** Emits the id of the step whose Start button was pressed. */
  readonly stepStart = output<string>();

  /** Emits the id of the step the user chose to skip. */
  readonly stepSkip = output<string>();

  /**
   * Expand/collapse was requested. The caller flips `collapsed`.
   *
   * Named `toggle` per the #20703 contract that the mounting components code against.
   * It shadows the native `toggle` DOM event (`<details>`/popover), which this component
   * never fires — Angular resolves `(toggle)` to this output on the component element.
   */
  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly toggle = output<void>();

  /** The user dismissed the whole panel. */
  readonly dismiss = output<void>();

  readonly isBanner = computed(() => this.mode() === 'banner');

  readonly totalCount = computed(() => this.steps().length);

  /** Only genuinely completed steps count towards progress — skipped is not 'afgerond'. */
  readonly completedCount = computed(() => this.steps().filter(step => step.state === 'completed').length);

  readonly progressLabel = computed(() => `${this.completedCount()} van ${this.totalCount()} afgerond`);

  readonly ringAriaLabel = computed(
    () => `Voortgang: ${this.completedCount()} van ${this.totalCount()} stappen afgerond`
  );

  /** The banner never collapses, so its steps are shown whenever it has any. */
  readonly showSteps = computed(() => (this.isBanner() || !this.collapsed()) && this.totalCount() > 0);

  /** Collapsed floating shows the title only; the banner always has room for the subtitle. */
  readonly showSubtitle = computed(() => !!this.config().subtitle && !this.collapsed());

  readonly toggleIcon = computed(() =>
    this.collapsed() ? 'feather:maximize-2' : 'feather:minimize-2'
  );

  readonly widthClass = computed(() => {
    if (this.isBanner()) {
      return 'w-full';
    }
    return this.collapsed() ? 'w-[278px] max-w-[calc(100vw-2rem)]' : 'w-[599px] max-w-[calc(100vw-2rem)]';
  });

  readonly headerPaddingClass = computed(() => {
    if (this.isBanner()) {
      return 'min-h-[92px] px-5 pb-4 pt-5';
    }
    return this.collapsed() ? 'pb-4 pl-4 pr-2 pt-2' : 'px-3 pb-4 pt-3';
  });

  readonly floatingContentPaddingClass = computed(() => (this.collapsed() ? 'pt-2' : 'px-2 pt-2'));

  readonly stepsPaddingClass = computed(() => 'px-5 pb-5');

  readonly stepRows = computed<StepRow[]>(() =>
    this.steps().map(step => ({
      ...step,
      indexLabel: String(step.index).padStart(2, '0'),
      isCompleted: step.state === 'completed',
      isSkipped: step.state === 'skipped',
      showStart: step.showStartButton && step.state !== 'completed',
      showSkip: step.skippable && step.state === 'open',
      startLabel: step.buttonLabel?.trim() || 'Start',
    }))
  );

  /**
   * Where the accordion starts. A missing, zero, negative or >= step-count `collapseAfter` means
   * "no accordion": folding away every step would leave a panel with nothing but a closed row,
   * which reads as a bug rather than a feature.
   *
   * `collapseAfter === steps.length - 1` — folding away exactly one step — IS allowed. It is odd
   * but unambiguous, and refusing it would silently ignore what the admin configured.
   */
  readonly #splitIndex = computed<number>(() => {
    const configured = this.config().collapseAfter;
    if (!configured || configured < 1 || configured >= this.stepRows().length) {
      return this.stepRows().length;
    }
    return configured;
  });

  readonly primaryRows = computed<StepRow[]>(() => this.stepRows().slice(0, this.#splitIndex()));

  readonly collapsedRows = computed<StepRow[]>(() => this.stepRows().slice(this.#splitIndex()));

  /** Accordion open/closed. Deliberately component-local: unlike the panel's own collapse this is
   *  a momentary reading choice, not a preference worth persisting per user. */
  readonly #groupOpen = signal(false);
  readonly groupOpen = this.#groupOpen.asReadonly();

  readonly groupLabel = computed(() => {
    const count = this.collapsedRows().length;
    const label = this.config().groupLabel?.trim() || 'Meer insteltaken';
    return `${label} (${count} ${count === 1 ? 'taak' : 'taken'})`;
  });

  /** Skipped does not count: the badge claims the folded work is done, and it is not. */
  readonly groupCompleted = computed(() => this.collapsedRows().every(row => row.isCompleted));

  readonly groupToggleIcon = computed(() =>
    this.groupOpen() ? 'heroicons_outline:chevron-up' : 'heroicons_outline:chevron-down'
  );

  /** Figma: 52px closed; 60px open. */
  readonly groupHeaderPaddingClass = computed(() => (this.groupOpen() ? 'h-[60px] py-3' : 'h-[52px] py-2'));

  toggleGroup(): void {
    this.#groupOpen.update(open => !open);
  }
}
